import { logger } from './logger';

// Configuration - Update this after deploying the CDK stack
// Remove trailing slash to avoid double slashes in URLs
const API_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, '');

// Auth token storage for API calls
// This is set by the AuthContext when user signs in
let authIdToken: string | null = null;

export function setAuthToken(token: string | null) {
  authIdToken = token;
}

export type SelectionReason = 'due' | 'semantic' | 'new' | 'reinforcement';
export type ContentType = 'personal' | 'curated';

export interface Flashcard {
  id: string;
  question: string;
  answer: string;
  isAIGenerated: boolean;
  sourceFile: string | null;
  rating?: Rating | 'never'; // Rating from previous session
  deck?: string | null; // Deck name
  selectionReason?: SelectionReason;
  selectionDetail?: string;
  contentType?: ContentType; // Whether this is personal or curated content
}

export interface Deck {
  id: string;
  name: string;
  cardCount: number;
  newCount: number;
  strugglingCount: number;
  type: ContentType; // 'personal' or 'curated'
}

export interface Stats {
  total: number;
  passed: number;
  lowRated: number;
  mediumRated: number;
  highRated: number;
  neverRated: number;
  activeFilters?: {
    decks: string[] | null;
  };
  unfilteredTotal?: number;
}

export interface NextQuestionsResponse {
  flashcards: Flashcard[];
  stats: Stats;
}

export type Rating = 'pass' | '1' | '2' | '3' | 'hide';

function getHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (authIdToken) {
    headers['Authorization'] = `Bearer ${authIdToken}`;
  }
  return headers;
}

export interface DeckFilters {
  personalDecks?: string[];
  curatedDecks?: string[];
}

export async function getNextQuestions(
  count: number = 20,
  filters?: DeckFilters
): Promise<NextQuestionsResponse> {
  try {
    const params = new URLSearchParams({ count: count.toString() });
    if (filters?.personalDecks && filters.personalDecks.length > 0) {
      params.set('personalDecks', filters.personalDecks.join(','));
    }
    if (filters?.curatedDecks && filters.curatedDecks.length > 0) {
      params.set('curatedDecks', filters.curatedDecks.join(','));
    }
    const url = `${API_URL}/questions/next?${params.toString()}`;
    logger.debug('Fetching from:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders(),
    });

    logger.debug('Response status:', response.status);

    if (!response.ok) {
      const error = await response.json();
      logger.error('API error response:', error);
      throw new Error(error.message || 'Failed to fetch questions');
    }

    const data = await response.json();
    logger.debug('API success response:', data);
    return data;
  } catch (error) {
    logger.error('Error fetching questions:', error);
    throw error;
  }
}

export interface DecksResponse {
  personalDecks: Deck[];
  curatedDecks: Deck[];
  decks: Deck[]; // Legacy field for backwards compatibility
}

export async function getDecks(): Promise<DecksResponse> {
  try {
    const url = `${API_URL}/decks`;
    logger.debug('Fetching decks from:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      logger.error('API error response:', error);
      throw new Error(error.message || 'Failed to fetch decks');
    }

    const data = await response.json();
    logger.debug('Decks response:', data);
    return data;
  } catch (error) {
    logger.error('Error fetching decks:', error);
    throw error;
  }
}

export interface SubmitRatingContext {
  previousRating?: string;
  selectionReason?: SelectionReason;
  deckId?: string;
  deckName?: string;
  question?: string;
}

export async function submitRating(
  flashcardId: string,
  rating: Rating,
  context?: SubmitRatingContext
): Promise<void> {
  try {
    const response = await fetch(`${API_URL}/ratings`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        flashcardId,
        rating,
        ...context,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to submit rating');
    }

    return await response.json();
  } catch (error) {
    logger.error('Error submitting rating:', error);
    throw error;
  }
}
